import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppContext } from 'src/components/app-context';
import { useProductContext } from 'src/areas/product/components/product-context';

import type { AppState } from 'src/reducers';

import { IconCompare } from '@prisjakt/ui/icon/compare';
import { IconHeartOutlined } from '@prisjakt/ui/icon/heart-outlined';
import { IconHeartFilled } from '@prisjakt/ui/icon/heart-filled';
import { IconNotificationFilled } from '@prisjakt/ui/icon/notification-filled';
import { IconNotificationOutlined } from '@prisjakt/ui/icon/notification-outlined';
import { ButtonSize, ButtonIcon } from '@prisjakt/ui/button/variants';

import {
  addItemToMyProductsRequest,
  addListItemRequest,
  selectList,
} from 'src/areas/sidebar/actions/lists'; // Should not depend on other areas

import { ListModal } from './list-modal';
import { useOnAction } from '../../utils';
import LoginPromoModal from 'src/components/modal/login-promo';

import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';

import type { UserProductAlert } from 'src/areas/sidebar/types';
import type { Me } from '../../types';

import {
  StyledTonalButton,
  StyledTooltip,
  ButtonText,
  ContentWrapper,
} from './styles';
import { ItemContainer } from './container';

import { Link } from 'react-router-dom';

type Props = {
  className?: string;
  isDesktop: boolean;
};

export const ActionButtons = (props: Props) => {
  const { className, isDesktop } = props;
  const { linkTo, t } = useAppContext();
  const { product } = useProductContext();

  const dispatch = useDispatch();

  const [addedToList, setAddedToListState] = useState<boolean>(false);
  const [listOpen, setListOpen] = useState(false);
  const [showLoginPromo, setLoginPromoState] = useState(false);

  const me: Me = useSelector((state: AppState) => state.areas.account.me);
  const userProductAlerts = useSelector(
    (state: AppState) => state.areas.sidebar.alerts.products
  );
  const deletedLists = useSelector(
    (state: AppState) => state.areas.sidebar.deletedLists
  );
  const selectedList = useSelector(
    (state: AppState) => state.areas.sidebar.selectedList
  );

  const isAuthenticated = useIsAuthenticated();
  const userAlert = userProductAlerts.find(
    (alert: UserProductAlert) => alert.productId === product.id
  );
  const alertId = userAlert && userAlert.alertId;
  const isBookmarked = alertId && isAuthenticated;
  const onAction = useOnAction();

  const latestLists =
    me?.lists?.nodes
      ?.filter(list => deletedLists[list.id] !== true)
      .filter(list => !list.inventory)
      .sort(a => {
        if (a.id === selectedList) {
          return 1;
        }

        return 0;
      }) ?? [];

  const handleListClick = () => setListOpen(state => !state);

  const handleAlertClick = () => onAction('alert-create');

  const addToList = (listId?: number) => {
    dispatch(selectList(listId));
    dispatch(addListItemRequest(product.id, product.name, isDesktop));
    onAction('list-add');
    setAddedToListState(true);
  };

  const addToMyProducts = () => {
    dispatch(addItemToMyProductsRequest(product.id, product.name, isDesktop));
    onAction('list-add');
    setAddedToListState(true);
  };

  const toggleLoginPromo = () => setLoginPromoState(!showLoginPromo);

  return (
    <div className={className}>
      {listOpen && (
        <ListModal
          addToList={addToList}
          addToMyProducts={addToMyProducts}
          isOpen={listOpen}
          closeModal={() => setListOpen(false)}
          lists={latestLists}
          isDesktop={isDesktop}
        />
      )}
      {showLoginPromo && (
        <LoginPromoModal
          isOpen={showLoginPromo}
          onRequestClose={toggleLoginPromo}
        />
      )}
      <ItemContainer>
        <>
          {/* Lists */}
          <StyledTooltip title={t('product', 'action-button.list-tooltip')}>
            <StyledTonalButton
              onClick={isAuthenticated ? handleListClick : toggleLoginPromo}
              containerProps={{
                'data-test': 'SaveToListButton',
              }}
              size={ButtonSize.SMALL}
            >
              <ContentWrapper>
                {addedToList ? (
                  <ButtonIcon size={16}>
                    <IconHeartFilled />
                  </ButtonIcon>
                ) : (
                  <ButtonIcon size={16}>
                    <IconHeartOutlined />
                  </ButtonIcon>
                )}
                {addedToList ? (
                  <ButtonText>{t('common', 'button.saved')}</ButtonText>
                ) : (
                  <ButtonText>{t('common', 'button.save')}</ButtonText>
                )}
              </ContentWrapper>
            </StyledTonalButton>
          </StyledTooltip>

          {/* Alerts */}
          <StyledTooltip title={t('product', 'action-button.alert-tooltip')}>
            <StyledTonalButton
              onClick={handleAlertClick}
              containerProps={{ 'data-test': 'CreateAlertButton' }}
              size={ButtonSize.SMALL}
            >
              <ContentWrapper>
                {isBookmarked ? (
                  <ButtonIcon size={16}>
                    <IconNotificationFilled />
                  </ButtonIcon>
                ) : (
                  <ButtonIcon size={16}>
                    <IconNotificationOutlined />
                  </ButtonIcon>
                )}

                <ButtonText>{t('product', 'alert.create-button')}</ButtonText>
              </ContentWrapper>
            </StyledTonalButton>
          </StyledTooltip>

          {/* Compare */}
          <StyledTooltip title={t('product', 'action-button.compare-tooltip')}>
            <StyledTonalButton
              container={Link}
              containerProps={{
                to: linkTo('productCompare', {
                  category: product.category,
                  products: [product],
                }),
                rel: 'nofollow',
                'data-test': 'CompareButton',
              }}
              size={ButtonSize.SMALL}
            >
              <ContentWrapper>
                <ButtonIcon size={16}>
                  <IconCompare />
                </ButtonIcon>
                <ButtonText>
                  {t('product', 'product-compare.compare')}
                </ButtonText>
              </ContentWrapper>
            </StyledTonalButton>
          </StyledTooltip>
        </>
      </ItemContainer>
    </div>
  );
};
